import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';

import { RegistrationHeader } from './components/RegistrationHeader';
import PersonalDetailsForm from './components/PersonalDetailsForm';
import AuthHeroLayout from './components/AuthHeroLayout';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import { useResendVerificationEmailMutation } from './services/registrationApi';
import { LoginConstants } from '../../config/keyClock.config';
import { showAlert } from '../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { IconComponent } from '../../util/icons';
import { useNavigationService } from '../../services/navigate-service';

import googleLogo from '../../asset/images/googleLogo.png';
import VerifyEmailImg from '../../asset/images/emailVerfication.jpg';

interface RegistrationResult {
  registered: boolean;
  userId: string;
  email: string;
}

const RegistrationPage = () => {
  const [result, setResult] = useState<RegistrationResult | null>(null);
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();
  const [resendEmail, { isLoading: resendLoading }] =
    useResendVerificationEmailMutation();

  const handleGoogleLogin = () => {
    sessionStorage.setItem(LoginConstants.IS_REGISTER, 'true');
    navigateTo('/');
  };

  const handleResendEmail = async () => {
    if (!result?.userId) return;
    try {
      await resendEmail(result.userId).unwrap();
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: 'An email has been sent successfully.',
        }),
      );
    } catch (error) {
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
    }
  };

  return (
    <AuthHeroLayout
      cardMaxWidth="max-w-[600px]"
      ariaLabelledBy="registration-page-heading"
    >
      <div className="space-y-6">
        <RegistrationHeader
          title="Sign Up"
          subtitle="Create your account with PlanOmatic"
          titleId="registration-page-heading"
        />

        {/* Google sign-up */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleLogin}
          className="w-full"
        >
          <IconComponent src={googleLogo} alt="" width={32} height={32} />
          Continue with Google
        </Button>

        {/* Divider — flanking rules with the label between them, so the line
            never shows through the text over the translucent card. */}
        <div className="flex items-center gap-3" aria-hidden>
          <div className="h-px flex-1 bg-border" />
          <span className="text-sm text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Form */}
        <PersonalDetailsForm onSuccess={setResult} />
      </div>

      {/* Verify email modal */}
      <Modal
        open={!!result?.registered}
        onOpenChange={(open) => !open && setResult(null)}
        size="sm"
        title="Verify Your Email"
        footer={
          <div className="flex w-full flex-col gap-1 items-center">
            <Typography as="p" variant="body-semibold">
              Still can&apos;t find the email? No problem.
            </Typography>
            <Button
              type="button"
              variant="link"
              disabled={resendLoading}
              onClick={handleResendEmail}
            >
              {resendLoading && <Loader2 className="animate-spin" />}
              Resend Email Verification
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <IconComponent src={VerifyEmailImg} alt="" width={85} height={85} />
          <Typography as="p" variant="body" className="text-muted-foreground">
            Please verify your email address by clicking the link sent to{' '}
            <Typography as="span" variant="body-semibold">
              {result?.email}
            </Typography>
            .
          </Typography>
          <Typography as="p" variant="body" className="text-muted-foreground">
            If you don&apos;t see it, you may need to check your spam folder.
          </Typography>
        </div>
      </Modal>
    </AuthHeroLayout>
  );
};

export default RegistrationPage;
